import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { ArrowRightIcon } from '@phosphor-icons/react';
import { PillButton, PillLink, Skeleton } from '@/components/kit';
import { ErrorState } from '@/components/QueryState';
import { publicApi } from '@/lib/api/public';
import { formatInStudio, formatLocalDay, formatPhoneHref, plural } from '@/lib/format';
import { useStudio } from '@/features/studio/studio-data';
import { BookingSummary } from '../BookingSummary';

const WINDOW_DAYS = 14;

export const availabilityKey = (slug: string, serviceId: string, from: string | null) =>
  ['availability', slug, serviceId, from] as const;

interface TimeStepProps {
  serviceId: string;
  day: string | null;
  startsAt: string | null;
  onDay: (day: string) => void;
  onPick: (slot: { startsAt: string; day: string }) => void;
  onNext: () => void;
  onChangeService: () => void;
}

const capital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const PARTS = [
  { label: 'Утро', test: (hour: number) => hour < 12 },
  { label: 'День', test: (hour: number) => hour >= 12 && hour < 17 },
  { label: 'Вечер', test: (hour: number) => hour >= 17 },
] as const;

/**
 * Day strip with the number of free windows per day, free times grouped into morning,
 * day and evening, and a footer button that shows the chosen date and time and moves
 * on. Picking a time only selects it, so the choice stays visible before «Далее».
 */
export function TimeStep({ serviceId, day, startsAt, onDay, onPick, onNext, onChangeService }: TimeStepProps) {
  const studio = useStudio();
  const service = studio.services.find((s) => s.id === serviceId);
  const [from, setFrom] = useState<string | null>(null);
  const strip = useRef<HTMLDivElement | null>(null);
  const query = useQuery({
    queryKey: availabilityKey(studio.slug, serviceId, from),
    queryFn: () => publicApi.availability(studio.slug, serviceId, from, WINDOW_DAYS),
    enabled: Boolean(service),
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  const tz = studio.timezone;
  const days = query.data?.days ?? [];
  const firstFree = days.find((d) => d.slots.length > 0)?.date;
  const activeDay = (day && days.some((d) => d.date === day) ? day : firstFree) ?? days[0]?.date ?? null;
  const active = days.find((d) => d.date === activeDay);
  const lastBookable = query.data?.lastBookableDay ?? null;
  const canLoadMore = query.data ? query.data.to < query.data.lastBookableDay : false;
  const nextFrom = query.data ? addDays(query.data.to, 1) : null;
  const chosen = startsAt && active?.slots.some((s) => sameInstant(s.startsAt, startsAt)) ? startsAt : null;

  // Keep the chosen day in view: the first free day can be past the visible part of the strip.
  useEffect(() => {
    const container = strip.current;
    const chosenDay = container?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!container || !chosenDay) return;
    const offset = chosenDay.getBoundingClientRect().left - container.getBoundingClientRect().left;
    const left = container.scrollLeft + offset - (container.clientWidth - chosenDay.clientWidth) / 2;
    container.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeDay, days.length]);

  if (!service) {
    return (
      <EmptyState
        title="Услуга недоступна"
        description="Её убрали из записи. Выберите другую."
        actions={
          <PillButton variant="primary" onClick={onChangeService}>
            Выбрать услугу
          </PillButton>
        }
        isCompact
      />
    );
  }

  const pickDate = (value: string) => {
    if (!value) return;
    if (!days.some((d) => d.date === value)) setFrom(value);
    onDay(value);
  };

  const groups = active
    ? PARTS.map((part) => ({
        label: part.label,
        slots: active.slots.filter((s) => part.test(Number(s.time.slice(0, 2)))),
      })).filter((g) => g.slots.length > 0)
    : [];

  return (
    <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-5)' }}>
      <BookingSummary service={service} currency={studio.currency} timezone={tz} onChange={onChangeService} />

      {query.isPending ? (
        <div className="ui-stack">
          <Skeleton height={78} label="Загружаем свободное время" />
          <Skeleton height={150} />
        </div>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} title="Не удалось загрузить расписание" />
      ) : !firstFree ? (
        <EmptyState
          title="Нет свободного времени"
          description={`В ближайшие ${WINDOW_DAYS} дней всё занято.`}
          actions={
            canLoadMore && nextFrom ? (
              <PillButton variant="primary" onClick={() => setFrom(nextFrom)}>
                Показать следующие дни
              </PillButton>
            ) : (
              <PillLink href={formatPhoneHref(studio.phone)}>Позвонить в студию</PillLink>
            )
          }
          isCompact
        />
      ) : (
        <>
          <div className="ui-stack" style={{ ['--ui-gap' as string]: '10px' }}>
            <div className="ui-month">
              <span className="ui-month__label">{activeDay ? capital(formatLocalDay(activeDay, 'LLLL')) : ''}</span>
              <label className="ui-month__pick">
                Календарь
                <input
                  type="date"
                  aria-label="Выбрать дату в календаре"
                  value={activeDay ?? ''}
                  min={days[0]?.date}
                  max={lastBookable ?? undefined}
                  onChange={(event) => pickDate(event.target.value)}
                />
              </label>
            </div>
            <div className="ui-days" role="group" aria-label="День" ref={strip}>
              {from ? (
                <button type="button" className="ui-day ui-day--more" onClick={() => setFrom(null)}>
                  Ближайшие
                </button>
              ) : null}
              {days.map((d) => {
                const count = d.slots.length;
                return (
                  <button
                    key={d.date}
                    type="button"
                    className="ui-day"
                    aria-pressed={d.date === activeDay}
                    aria-label={`${formatLocalDay(d.date, 'EEEE, d MMMM')}${count ? `, свободно ${count}` : ', нет мест'}`}
                    disabled={count === 0}
                    onClick={() => onDay(d.date)}
                    data-testid={`day-${d.date}`}
                  >
                    <span className="ui-day__dow">{capital(formatLocalDay(d.date, 'EEEEEE'))}</span>
                    <span className="ui-day__num">{formatLocalDay(d.date, 'd')}</span>
                    <span className="ui-day__count" data-few={count > 0 && count <= 2 ? 'true' : undefined}>
                      {count ? `${count} ${plural(count, 'окно', 'окна', 'окон')}` : 'нет мест'}
                    </span>
                  </button>
                );
              })}
              {canLoadMore && nextFrom ? (
                <button type="button" className="ui-day ui-day--more" onClick={() => setFrom(nextFrom)}>
                  Ещё дни
                </button>
              ) : null}
            </div>
          </div>

          {active && groups.length > 0 ? (
            <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
              {groups.map((group) => (
                <div key={group.label} className="ui-stack" style={{ ['--ui-gap' as string]: '8px' }}>
                  <span className="ui-time-group">{group.label}</span>
                  <div
                    className="ui-times ui-times--4"
                    role="group"
                    aria-label={`${group.label}, ${formatLocalDay(active.date, 'd MMMM')}`}
                  >
                    {group.slots.map((slot) => (
                      <button
                        key={slot.startsAt}
                        type="button"
                        className="ui-time"
                        aria-pressed={chosen !== null && sameInstant(chosen, slot.startsAt)}
                        onClick={() => onPick({ startsAt: slot.startsAt, day: active.date })}
                        data-testid={`slot-${slot.time}`}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="ui-muted">На этот день студия закрыта или свободных окон нет.</p>
          )}

          <p className="ui-caption">Время указано по часовому поясу студии.</p>

          <div className="ui-sheet-actions">
            <button type="button" className="ui-next" disabled={!chosen} onClick={onNext} data-testid="time-next">
              {chosen ? (
                <span className="ui-next__when">
                  <span className="ui-next__day">{capital(formatInStudio(chosen, tz, 'EEEEEE, d MMMM'))}</span>
                  <span className="ui-next__time ui-num">{timeRange(chosen, service.durationMinutes, tz)}</span>
                </span>
              ) : (
                <span className="ui-next__when">
                  <span className="ui-next__time">Выберите время</span>
                </span>
              )}
              <span className="ui-next__go">
                Далее
                <ArrowRightIcon size={20} weight="bold" aria-hidden />
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function sameInstant(a: string, b: string) {
  return new Date(a).getTime() === new Date(b).getTime();
}

/** «14:00 — 16:00» within a day; «14:00 · выдача 11 окт» when the car stays overnight. */
function timeRange(startsAt: string, minutes: number, tz: string) {
  const end = new Date(new Date(startsAt).getTime() + minutes * 60_000).toISOString();
  const start = formatInStudio(startsAt, tz, 'HH:mm');
  const sameDay = formatInStudio(startsAt, tz, 'yyyy-MM-dd') === formatInStudio(end, tz, 'yyyy-MM-dd');
  return sameDay ? `${start} — ${formatInStudio(end, tz, 'HH:mm')}` : `${start} · выдача ${formatInStudio(end, tz, 'd MMM')}`;
}

function addDays(isoDate: string, days: number) {
  const date = new Date(`${isoDate}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
